import {
  CountryCode,
  HumanNamePrefix,
  LanguagePreference,
  PersonIdentityCode,
} from '@quromedical/fhir-common';
import type { Person as FhirPerson } from 'fhir/r4';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { z } from 'zod';

export type Gender = NonNullable<FhirPerson['gender']>;

export interface Person {
  general: PersonGeneral;
  contact: PersonContact;
}

export const GenderSchema = z.union([
  z.literal('unknown'),
  z.literal('male'),
  z.literal('female'),
  z.literal('other'),
]);

export const GeneralSchema = z.object({
  prefixName: z.nativeEnum(HumanNamePrefix),
  givenName: z.string(),
  familyName: z.string(),
  gender: GenderSchema,
  birthDate: z.string(),
  identifierCode: z.nativeEnum(PersonIdentityCode),
  identifierValue: z.string(),
});

export const GeneralUpdateSchema = z.object({
  id: z.string().uuid(),
  general: GeneralSchema,
});

export type PersonGeneral = z.infer<typeof GeneralSchema>;
export type GeneralUpdate = z.infer<typeof GeneralUpdateSchema>;

export const PersonGeneralGetResponseSchema = GeneralSchema.partial();
export type PersonGeneralGetResponse = z.infer<
  typeof PersonGeneralGetResponseSchema
>;

export const AddressEmergencyAccessSchema = z.object({
  addressEmergencyAccess: z.string(),
});

export const CoordinatesSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

const phoneErrorMsg =
  'Must be in international format (e.g., +27823456789, +12125551234)';

export const internationalPhoneNumberSchema = z
  .string()
  .transform((val, ctx) => {
    const phoneNumber = parsePhoneNumberFromString(val);

    if (!phoneNumber || !phoneNumber.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: phoneErrorMsg,
        path: ['phoneNumber', val],
      });
      return;
    }

    return phoneNumber.number;
  });

export const PrimaryPhoneNumberSchema = internationalPhoneNumberSchema;

export const AlternatePhoneNumberSchema =
  internationalPhoneNumberSchema.optional();

export const telecomPhoneNumbersSchema = z
  .array(AlternatePhoneNumberSchema)
  .transform(
    (numbers) => numbers.filter((num) => num !== undefined) as string[],
  )
  .refine((lines) => PrimaryPhoneNumberSchema.safeParse(lines[0]).success, {
    message:
      'The primary phone number must be in international phone number format +27XXXXXXXXX',
  });

export const ContactSchema = z.object({
  languagePreference: z.nativeEnum(LanguagePreference).optional(),
  /**
   * In formik you can reference this as `phoneNumbers.0` and `phoneNumbers.1` to populate array
   * values if there isn't an appropriate multi-text input
   */
  telecomPhoneNumbers: telecomPhoneNumbersSchema,
  telecomEmail: z.string(),
  /**
   * Google address place_Id is from google place autocomplete
   */
  addressPlaceId: z.string().optional(),
  addressCoordinates: CoordinatesSchema.optional(),
  addressLines: z.array(z.string()),
  addressCity: z.string(),
  addressCountry: z.nativeEnum(CountryCode),
  addressPostalCode: z.string(),
  addressEmergencyAccess: z.string().optional(),
});

export const ContactGetSchema = ContactSchema.omit({
  telecomPhoneNumbers: true,
}).extend({
  telecomPhoneNumbers: z.array(z.string()),
});

export type Contact = z.infer<typeof ContactSchema>;

export const ContactUpdateSchema = z.object({
  id: z.string().uuid(),
  contact: ContactSchema,
});
export type ContactUpdate = z.infer<typeof ContactUpdateSchema>;

export const PartialPersonSchema = z.object({
  general: GeneralSchema.partial(),
  contact: ContactGetSchema.partial(),
});

export const PersonSchema = z.object({
  general: GeneralSchema,
  contact: ContactSchema,
});

export const PersonContactWithAddressEmergencyAccessSchema = ContactSchema.and(
  AddressEmergencyAccessSchema.partial(),
);

export type PersonContactWithAddressEmergencyAccess = z.infer<
  typeof PersonContactWithAddressEmergencyAccessSchema
>;
export type AddressEmergencyAccess = z.infer<
  typeof AddressEmergencyAccessSchema
>;
export type Coordinates = z.infer<typeof CoordinatesSchema>;

export type PartialPerson = z.infer<typeof PartialPersonSchema>;

export type PersonContact = z.infer<typeof ContactSchema>;

export const PersonContactGetResponseSchema = ContactGetSchema.partial();
export type PersonContactGetResponse = z.infer<
  typeof PersonContactGetResponseSchema
>;
